import { Tooltip } from 'antd';
import NavToolTip from 'common/navigation/components/menu-tooltip';
import PropTypes from 'prop-types';
import React from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import styled, { withTheme } from 'styled-components';
import { Icon, media, SapId } from 'ui';
import { referenceTypes } from '../../../utils/app-configs';
import { setPromptInfo } from '../containers/navigation-actions';

export const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  font-size: 16px !important;
  white-space: nowrap;
`;

const SmallText = styled.div`
  display: flex;
  flex-flow: row nowrap;
  font-size: 0.5rem;
  margin-top: -10px;
  white-space: nowrap;
`;

export const Item = styled.li`
  &:hover{
    a, i {
      color: ${p => p.theme.colors.theme};
    }
  }
  position: relative;
  text-transform: uppercase;
  ${p => !p.isMain ? 'min-width: 0;' : ''}
  ${media.tablet`
    border-bottom: 1px solid ${p => p.theme.colors.border};
    width: 100%;
    padding: 5px 20px 0;
    &:last-child {
      border-bottom: none;
    }
  `};
  display: flex;
  align-items: center;
  height: 3rem;
  font-weight: ${p => p.theme.fontWeight.bolder};
  cursor: ${p => p.allowRedirect ? 'pointer' : 'not-allowed'};
  a {
    color: ${p => p.theme.colors.text};
    padding: 13px 15px 7px 15px;
    min-width: 0;
    border-bottom: 3px solid transparent;
    text-decoration: none;
    height: 100%;
    &:hover {
      color: ${p => p.theme.colors.theme};
    }
  }
`;

const InnerWrapper = styled.p`
  color: ${p => p.theme.colors.text};
  padding: 13px 15px 7px 15px;
  min-width: 0;
  border-bottom: 3px solid transparent;
  text-decoration: none;
  height: 100%;
  > div:first-child {
    i {
      cursor: pointer;
      color: #B2C1CF !important;
    }
  }
`;

const NavContextButton = styled.div`
  display: inline-flex;
  max-width: 100%;
  white-space: nowrap;
  div {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const InfoIcon = styled(Icon)`
  color: #B2C1CF;
  font-size: 11px;
  margin-left: 2%;
  margin-top: 4px;
  cursor: pointer;
`;

const StyledSpinner = styled(Icon)`
  position: absolute !important;
  top: 22px;
  right: 0 !important;
`;

const getToolTipContent = toolTipData =>
  <NavToolTip
    toolTipData={toolTipData}
  />;

const getInnerBody = (type, title, toolTipData) =>
  <TextWrapper>
    <SmallText>
      {type}
      {toolTipData && Object.keys(toolTipData)?.length ?
        <InfoIcon type='info-circle' size='sm' /> :
        null
      }
    </SmallText>
    <NavContextButton data-test-id='menu-item.context'>
      <div>
        <SapId originalId={title}>{id => <>{id}</>}</SapId>
      </div>
    </NavContextButton>
  </TextWrapper>;


const getLinkBody = (hasType, type, title, toolTipData) => {
  if (hasType) {
    // Try to transform title to Id.
    // If the title is not numerical, it will just display the title.
    // Otherwise, if it would be a SAP ID (e.g with order),
    // the leading zeroes will be deleted
    if (toolTipData && Object.keys(toolTipData)?.length) {
      return (
        <Tooltip
          title={getToolTipContent(toolTipData)}
          key={`tooltip${type}`}
          mouseEnterDelay={0.5}
          placement='bottom'
          color='white'
          overlayInnerStyle={{
            color: 'black',
          }}
          overlayStyle={{
            'max-width': '100%',
          }}
        >
          {getInnerBody(type, title, toolTipData)}
        </Tooltip>
      );
    }
    return (
      <>
        {getInnerBody(type, title)}
      </>
    );
  }

  return <TextWrapper>{type}</TextWrapper>;
};

const getActiveStyle = theme => ({
  color: theme.colors.theme,
  borderBottomColor: theme.colors.theme,
});

const MenuItem = ({
  type,
  link,
  title,
  toolTip,
  hasType = false,
  theme,
  active = false,
  translationKey,
  loading = false,
  itemType,
}) => {
  const location = useLocation();
  const { isNavigationBlocked, promptInfo } = useSelector(state => state.navigation);
  const dispatch = useDispatch();

  const checkIfActive = () => {
    if (link.includes('/tickets')) {
      return location.pathname.startsWith('/tickets');
    }

    return decodeURIComponent(location.pathname).includes(
      decodeURIComponent(link),
    );
  };

  const isCurrentLinkActive = checkIfActive();

  const navigate = (e) => {
    if (isNavigationBlocked) {
      e.preventDefault();
      if (!promptInfo) {
        dispatch(setPromptInfo({
          url: link,
        }));
      }
    }
  };
  const allowRedirect = itemType?.toLowerCase() === referenceTypes.functionallocation ?
    isToggleEnabled('functionalLocationInfo') :
    true;

  if (!allowRedirect) {
    return <Item data-test-id={translationKey} allowRedirect={false} isMain={!hasType}>
      <InnerWrapper>
        {getLinkBody(hasType, type, title, toolTip)}
        {loading && <StyledSpinner className='fa-solid fa-spinner fa-spin' size='xs' />}
      </InnerWrapper>
    </Item>;
  }

  return <Item data-test-id={translationKey} allowRedirect={allowRedirect} isMain={!hasType}>
    <NavLink
      to={link}
      onClick={navigate}
      style={({ isActive }) =>
        isActive || isCurrentLinkActive ?
          // eslint-disable-next-line no-undefined
          getActiveStyle(theme) : undefined
      }
      isActive={active ? () => true : null}
    >
      {getLinkBody(hasType, type, title, toolTip)}
      {loading && <StyledSpinner className='fa-solid fa-spinner fa-spin' size='xs' />}
    </NavLink>
  </Item>;
};
MenuItem.propTypes = {
  type: PropTypes.string,
  link: PropTypes.string,
  title: PropTypes.string,
  toolTip: PropTypes.object,
  hasType: PropTypes.bool,
  theme: PropTypes.object,
  active: PropTypes.bool,
  translationKey: PropTypes.string,
  loading: PropTypes.bool,
  itemType: PropTypes.string,
};

export default withTheme(MenuItem);
